import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Select, TimePicker, Row, Col } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import 'antd/dist/antd.css';
import './calendar-entry-modal.scss';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import { getClasse } from '../classe/classe.reducer';
import { createCalendarEntry, getCalendar, getCalendarEntry, reset, updateCalendarEntry } from './calendarEntry.reducer';
import { getEntities } from '../matiere-ecole/matiere-ecole.reducer';
import { getProfesseurs } from '../personnel/personnel.reducer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { JourSemaine } from './calendar-constant';
import moment from 'moment';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

export const CalendarEntryModal = (
  props: RouteComponentProps<{ id: string; classeid: string; ecoleid: string; anneeScolaireId: string }>
) => {
  const dispatch = useAppDispatch();
  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const matieres = useAppSelector(state => state.matiereEcole.entities);
  const professeurs = useAppSelector(state => state.professeur.entities);
  const calendarEntry = useAppSelector(state => state.calendarEntry.entity);
  const id = props.match.params.id;
  const ecoleid = props.match.params.ecoleid;
  const classeid = props.match.params.classeid;
  const [heureDebut, setHeureDebut] = useState<string>();
  const [heureFin, setHeureFin] = useState<string>();
  const [selectedProfesseurs, setSelectedProfesseurs] = useState([]);
  const [form] = Form.useForm();

  const location = useLocation();
  const isDuplicate = location.pathname.includes('duplicate');

  useEffect(() => {
    dispatch(getCalendarEntry(props.match.params.id));
    const entity = {
      anneeScolaireId: props.match.params.anneeScolaireId,
      classeid: props.match.params.classeid,
    };
    dispatch(getCalendar(entity));
    dispatch(getEntities({ ecoleId: ecoleid, page: 0, size: 100 }));
    dispatch(getProfesseurs({ ecoleId: ecoleid, page: 0, size: 100, typePersonnel: 'ENSEIGNANT' }));
  }, []);

  useEffect(() => {
    if (calendarEntry?.id) {
      form.setFieldsValue({
        jour: calendarEntry.jour,
        matiere: { id: calendarEntry?.matiere?.id },
      });
      setSelectedProfesseurs((calendarEntry.professeurs ?? []).map(p => ({ id: p.id })));
      setHeureDebut(calendarEntry.heureDebut);
      setHeureFin(calendarEntry.heureFin);
    }
  }, [calendarEntry]);

  const handleTimeChange = (times, timeStrings) => {
    if (times && times.length === 2) {
      setHeureDebut(timeStrings[0]);
      setHeureFin(timeStrings[1]);
    }
  };

  const handleClose = () => {
    props.history.push(`/calendar/${props.match.params.ecoleid}/${props.match.params.anneeScolaireId}/${classeid}/calendarEntry`);
  };

  const saveEntity = values => {
    const entity = isDuplicate
      ? {
          ...values,
          classeId: props.match.params.classeid,
          anneeScolaireId: props.match.params.anneeScolaireId,
          professeurs: selectedProfesseurs,
          heureDebut,
          heureFin,
        }
      : {
          ...calendarEntry,
          ...values,
          classeId: props.match.params.classeid,
          professeurs: selectedProfesseurs,
          anneeScolaireId: props.match.params.anneeScolaireId,
          heureDebut,
          heureFin,
          matiere: { ...values.matiere },
        };

    isDuplicate ? dispatch(createCalendarEntry(entity)) : dispatch(updateCalendarEntry(entity));
    handleClose();
  };

  const goBack = () => {
    props.history.goBack();
  };

  const handleProfesseur = data => {
    setSelectedProfesseurs(data.map(p => ({ id: p })));
  };

  const onDelete = () => {
    props.history.push(`/calendar/${ecoleid}/${props.match.params.anneeScolaireId}/${classeid}/calendarEntry/${id}/delete`);
  };

  return (
    <>
      {calendarEntry.id !== null && (
        <Modal
          title={isDuplicate ? "Dupliquer l'emploi du temps" : "Modifier l'emploi du temps"}
          className="calendar-entry-modal"
          centered
          footer={
            <Row justify="space-between" gutter={[16, 16]}>
              <Col>
                <Button key="back" className="delete-btn" onClick={onDelete}>
                  <FontAwesomeIcon icon={faTrash} title="Supprimer cours" />
                  &nbsp; Supprimer
                </Button>
              </Col>
              <Col>
                <Row>
                  <Button key="back" className="cancel-btn" onClick={goBack}>
                    Annuler
                  </Button>
                  <Button
                    onClick={() => form.submit()}
                    className="save-btn"
                    data-cy="entityCreateSaveButton"
                    htmlType="button"
                    id="save-entity"
                  >
                    <FontAwesomeIcon icon="save" />
                    &nbsp; {isDuplicate ? 'Dupliquer' : 'Modifier'}
                  </Button>
                </Row>
              </Col>
            </Row>
          }
          visible={true}
          closable={false}
        >
          <Form
            form={form}
            className="calendar-entry-form"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 14 }}
            layout="horizontal"
            initialValues={{}} // Important pour éviter des erreurs de rendu initial
            onFinish={saveEntity}
          >
            <Form.Item label="Jour" name="jour" rules={[{ required: true }]}>
              <Select placeholder="Jour de cours" style={{ width: '100%' }} optionFilterProp="children">
                {JourSemaine.map((jour, i) => (
                  <Select.Option key={i} value={i}>
                    {jour}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Heures">
              <TimePicker.RangePicker
                format="HH:mm"
                allowClear
                showNow={false}
                onChange={handleTimeChange}
                disabledTime={() => ({
                  disabledHours: () => [...Array(24).keys()].filter(i => i < 7 || i > 19),
                })}
                defaultValue={[
                  calendarEntry.heureDebut ? moment(calendarEntry.heureDebut, 'HH:mm') : null,
                  calendarEntry.heureFin ? moment(calendarEntry.heureFin, 'HH:mm') : null,
                ]}
              />
            </Form.Item>

            <Form.Item label="Matière" name={['matiere', 'id']} rules={[{ required: true }]}>
              <Select placeholder="Matière" style={{ width: '100%' }} optionFilterProp="children" showSearch>
                {matieres.map(matiere => (
                  <Select.Option key={matiere.id} value={matiere.id}>
                    {matiere.nom}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Professeur">
              <Select
                placeholder="Professeur"
                mode="multiple"
                onChange={handleProfesseur}
                defaultValue={(calendarEntry?.professeurs ?? []).map(p => p.id)}
                style={{ width: '100%' }}
                optionFilterProp="children"
              >
                {professeurs
                  .filter(prof => prof.typePersonnel === 'ENSEIGNANT')
                  .map(prof => (
                    <Select.Option key={prof.id} value={prof.id}>
                      {prof.prenom} {prof.nom}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default CalendarEntryModal;
